/**
 * The floating feedback button — present on every screen, and the only way a
 * normal user ever gives feedback.
 *
 * The form is NOT written here. The button asks the server which definition
 * applies to the screen you are on, and hands whatever comes back to
 * FeedbackFormRenderer. So "the form changes per screen" needs no code: adding a
 * definition for a screen changes what the button opens, and adding a question to
 * that definition changes what it asks.
 *
 * Resolution is most-specific-first, server-side (feedback_master_repository.resolve):
 *
 *     this project + this screen  (componentCode = the screen's nav id)
 *     -> this project, any screen (componentCode null)
 *     -> the global default:screen
 *
 * which is why the button works on a screen nobody has configured: the global
 * default catches it. If even that is missing the button hides itself rather than
 * opening a form that cannot be submitted.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Button } from 'sharedUi/Button';
import { Message } from 'sharedUi/Message';
import { Dialog } from 'sharedUi/Dialog';
import { feedbackMasterService } from '../../services/feedback-master.service';
import { feedbackSubmissionService } from '../../services/feedback-submission.service';
import { useToastApi } from '../../hooks/use-toast-api';
import { PERMISSIONS } from '../../constants/permissions';
import FeedbackFormRenderer, { type AnswerMap } from './feedback-form-renderer';
import { screenCodeForPath } from './screen-code';
import { definitionKeyForScreen } from './feedback-components';

const FeedbackFab = () => {
  const { pathname } = useLocation();
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();

  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [submitted, setSubmitted] = useState(false);

  const permKeys = useSelector((s: { auth?: { permissions?: string[] } }) => s.auth?.permissions || []);
  const canGive = useMemo(
    () => new Set(permKeys).has(PERMISSIONS.FEEDBACK_SUBMISSION_CREATE),
    [permKeys],
  );

  const screenCode = screenCodeForPath(pathname);
  // An explicit pin from the registry, when this screen has one. Usually null,
  // which leaves the server's own most-specific-first resolution to decide.
  const pinnedKey = definitionKeyForScreen(screenCode);

  /* Resolved per screen. `retry: false` because a 404 here is an ANSWER — this
     screen has no form — not a failure worth retrying three times on every
     navigation. */
  const defQ = useQuery({
    queryKey: ['feedback-resolve', 'SCREEN', screenCode, pinnedKey],
    queryFn: () => feedbackMasterService.resolve(
      'SCREEN', pinnedKey || undefined, screenCode || undefined),
    enabled: canGive && !!screenCode,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const definition = defQ.data;

  // A different screen is a different form; anything half-typed belonged to the
  // screen it was typed on.
  useEffect(() => {
    setOpen(false);
    setAnswers({});
    setSubmitted(false);
  }, [screenCode]);

  /** Only the rules the SERVER also enforces, so the two can never disagree. */
  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    for (const q of definition?.questions || []) {
      if (!q.required) continue;
      const v = answers[q.questionCode]?.value;
      const empty = v === undefined || v === null || v === ''
        || (Array.isArray(v) && v.length === 0);
      if (empty) e[q.questionCode] = 'This one is required.';
    }
    return e;
  }, [definition, answers]);

  const answered = Object.entries(answers).filter(([, a]) => {
    const v = a.value;
    return !(v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0));
  });

  const sendM = useMutation({
    mutationFn: () => feedbackSubmissionService.create({
      feedbackDefinitionId: definition!._id,
      answers: answered.map(([questionCode, a]) => ({
        questionCode,
        value: a.value,
        comment: a.comment?.trim() || null,
      })),
      component: {
        // The full path, record id included: it is what tells a reviewer WHICH
        // record the complaint was about, not just which screen.
        componentId: pathname,
        componentVersion: null,
      },
      sessionContext: { route: pathname },
    }),
    onSuccess: () => {
      ok('Thanks — your feedback was sent');
      setOpen(false);
      setAnswers({});
      setSubmitted(false);
      qc.invalidateQueries({ queryKey: ['feedback-my-submissions'] });
      qc.invalidateQueries({ queryKey: ['feedback-assigned-to-me'] });
      qc.invalidateQueries({ queryKey: ['feedback-submissions'] });
      qc.invalidateQueries({ queryKey: ['feedback-reviews'] });
      qc.invalidateQueries({ queryKey: ['feedback-review-inbox'] });
    },
    onError: (e) => fail(e),
  });

  const send = () => {
    setSubmitted(true);
    if (!definition || Object.keys(errors).length || !answered.length) return;
    sendM.mutate();
  };

  // No permission, not a menu screen, or nothing resolves → no button at all.
  // A button that opens an empty panel is worse than no button.
  if (!canGive || !screenCode || !definition) return null;

  return (
    <>
      {toast}

      <Dialog
        visible={open}
        onHide={() => setOpen(false)}
        header="Give feedback"
        style={{ width: 560, maxWidth: '95vw' }}
        modal
        dismissableMask
        data-testid="FeedbackFab.Dialog"
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Cancel" severity="secondary" outlined
              onClick={() => setOpen(false)} disabled={sendM.isPending} />
            <Button label="Send" icon="pi pi-send"
              onClick={send} disabled={sendM.isPending} data-testid="FeedbackFab.Send" />
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <FeedbackFormRenderer
            definition={definition}
            answers={answers}
            onChange={setAnswers}
            submitted={submitted}
            errors={errors}
          />
          {submitted && !answered.length && (
            <Message severity="error" className="w-full justify-start"
              text="Answer at least one question before sending." />
          )}
        </div>
      </Dialog>

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Give feedback about this screen"
        title="Give feedback about this screen"
        data-testid="FeedbackFab"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
      >
        <i className={`pi ${open ? 'pi-times' : 'pi-comment'} text-xl`} />
      </button>
    </>
  );
};

export default FeedbackFab;
